import type { FC, PropsWithChildren } from 'react';
import type { GameLikeButtonIconSize, GameLikeButtonPosition } from './components/GameLikeButton';
import type { MediaDto } from '@/queries/media/media.schema';
import { AppImage } from '@/components/ui/AppImage/AppImage';
import { Link } from '@/i18n/routing';
import { cn } from '@/utils/cn';
import GameLikeButton from './components/GameLikeButton';

export type GameCardVariant = 'game-cover-small' | 'game-cover-grid';

export type GameCardProps = {
  id: string;
  name: string;
  path: string;
  className?: string;
  showName?: boolean;
  pictureClassName?: string;
  cover?: MediaDto | null;
  priority?: boolean;
  variant?: GameCardVariant;
  likeButtonPosition?: GameLikeButtonPosition;
  likeButtonIconSize?: GameLikeButtonIconSize;
  isFavorited?: boolean;
} & PropsWithChildren;

const GameCard: FC<GameCardProps> = (props) => {
  const {
    id,
    path,
    name,
    cover,
    className,
    pictureClassName,
    showName,
    priority,
    variant = 'game-cover-grid',
    children,
    likeButtonPosition = 'default',
    likeButtonIconSize = 'default',
    isFavorited,
  } = props;

  return (
    <div className={cn('flex flex-col transition-transform will-change-transform duration-200 hover:scale-105 transform-gpu py-1 relative', className)}>
      <GameLikeButton
        gameId={id}
        position={likeButtonPosition}
        iconSize={likeButtonIconSize}
        isFavorited={isFavorited}
      />

      <Link
        href={path}
        prefetch={false}
        className="flex flex-col gap-1.75"
      >
        {cover && (cover.key || cover.path) && (
          <div
            className={cn(
              'w-full aspect-3/4 overflow-hidden rounded-xl lg:w-30 bg-c870',
              pictureClassName,
            )}
          >
            <AppImage
              src={cover.key ?? cover.path}
              alt={cover?.meta?.alt || name}
              variant={variant}
              mimeType={cover.mimeType}
              priority={priority}
              className="w-full h-full"
            />
          </div>
        )}

        {showName && <p style={{ perspective: '1px' }} className="will-change-transform subpixel-antialiased text-sm font-semibold capitalize text-light">{name}</p>}
      </Link>
      {children}
    </div>
  );
};

GameCard.displayName = 'GameCard';
export default GameCard;
